import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Tooltip,
  Checkbox,
} from '@mui/material';
import {
  Download as DownloadIcon,
  ArrowBack as ArrowBackIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  BookmarkBorder as SaveIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { renderCompactLink } from '../utils/formatters';

// Helper function to truncate text
const truncateText = (text: string, maxLength: number = 30) => {
  if (!text) return '-';
  return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
};

// Mock data for demonstration - GrowMeOrganic API results
const generateMockResults = (count: number) => {
  const results = [];
  const baseData = [
    {
      domain: 'techcorp.com',
      company: 'Tech Corp',
      title: 'Technology Solutions',
      emailCount: 150,
      description: 'Leading technology company specializing in software development and cloud services',
      type: 'B2B',
      c_tag1: 'Technology',
      c_tag2: 'Software',
      c_tag3: 'Cloud',
      c_tag4: '',
      notes: ''
    },
    {
      domain: 'healthinc.com',
      company: 'Health Inc',
      title: 'Healthcare Technology',
      emailCount: 89,
      description: 'Healthcare technology solutions provider focused on patient care systems',
      type: 'B2C',
      c_tag1: 'Healthcare',
      c_tag2: 'Technology',
      c_tag3: 'Medical',
      c_tag4: '',
      notes: 'High priority'
    },
    {
      domain: 'cannabisco.com',
      company: 'Cannabis Co',
      title: 'Cannabis Consulting',
      emailCount: 234,
      description: 'Cannabis industry consulting and compliance services for businesses',
      type: 'B2B',
      c_tag1: 'Cannabis',
      c_tag2: 'Consulting',
      c_tag3: 'CBD',
      c_tag4: 'THC',
      notes: 'Verified contact'
    },
    {
      domain: 'greentech.io',
      company: 'Green Tech Solutions',
      title: 'Sustainable Technology',
      emailCount: 176,
      description: 'Green energy and sustainable technology solutions for modern businesses',
      type: 'B2B',
      c_tag1: 'Green Tech',
      c_tag2: 'Sustainability',
      c_tag3: 'Energy',
      c_tag4: '',
      notes: ''
    },
    {
      domain: 'investorhub.com',
      company: 'Investor Hub',
      title: 'Investment Platform',
      emailCount: 312,
      description: 'Investment platform connecting startups with angel investors and VCs',
      type: 'B2B',
      c_tag1: 'Investment',
      c_tag2: 'Finance',
      c_tag3: 'Venture Capital',
      c_tag4: '',
      notes: 'Follow up required'
    },
  ];

  for (let i = 0; i < count; i++) {
    const base = baseData[i % baseData.length];
    results.push({
      id: i + 1,
      ...base,
      domain: i >= baseData.length ? `${base.domain.split('.')[0]}${i}.${base.domain.split('.')[1]}` : base.domain,
      company: i >= baseData.length ? `${base.company} ${i}` : base.company,
    });
  }
  return results;
};

const mockResults = generateMockResults(30);

const OutreachResultsPage: React.FC = () => {
  const navigate = useNavigate();
  const [displayCount, setDisplayCount] = useState(20);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // State for selection
  const [selectedResults, setSelectedResults] = useState<Set<number>>(new Set());
  const [selectAll, setSelectAll] = useState(false);
  
  // State for sorting
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Infinite scroll
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      if (scrollTop + clientHeight >= scrollHeight - 10) {
        setDisplayCount(prev => Math.min(prev + 20, mockResults.length));
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle sorting
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Sort results
  const sortedResults = React.useMemo(() => {
    if (!sortField) return mockResults;
    
    return [...mockResults].sort((a: any, b: any) => {
      const aValue = a[sortField];
      const bValue = b[sortField];
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [sortField, sortDirection]);

  // Handle select all
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedResults(new Set());
    } else {
      const allIds = mockResults.slice(0, displayCount).map(r => r.id);
      setSelectedResults(new Set(allIds));
    }
    setSelectAll(!selectAll);
  };

  // Handle individual selection
  const handleSelectResult = (id: number) => {
    const newSelected = new Set(selectedResults);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedResults(newSelected);
    setSelectAll(newSelected.size === displayCount);
  };

  const handleBack = () => {
    navigate('/outreach/domain-list');
  };

  const handleDownload = () => {
    // In a real app, this would download the results as CSV or Excel
  };

  const handleValidate = () => {
    navigate('/outreach/validation');
  };

  const handleSaveForLater = () => {
    // In a real app, this would save the selected results to a database or local storage
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Section */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            Company Results
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Total results: {mockResults.length}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<SaveIcon />}
            size="small"
            onClick={handleSaveForLater}
            disabled={selectedResults.size === 0}
          >
            SAVE FOR LATER ({selectedResults.size})
          </Button>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            size="small"
            onClick={handleBack}
          >
            BACK
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            size="small"
            onClick={handleDownload}
          >
            DOWNLOAD
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleValidate}
          >
            VALIDATE
          </Button>
        </Box>
      </Box>

      {/* Selection Controls */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {selectedResults.size > 0 ? `${selectedResults.size} selected` : 'No items selected'}
        </Typography>
        <Typography
          variant="body2"
          sx={{ 
            color: 'primary.main', 
            cursor: 'pointer',
            '&:hover': { textDecoration: 'underline' }
          }}
          onClick={handleSelectAll}
        >
          {selectAll ? 'Deselect All' : 'Select All'}
        </Typography>
      </Box>

      {/* Table Section */}
      <Paper sx={{ mb: 4 }}>
        <TableContainer 
          ref={containerRef}
          sx={{ 
            maxHeight: 'none',
            overflow: 'auto'
          }}
        >
          <Table 
            stickyHeader
            sx={{ 
              '& .MuiTableCell-root': { padding: '6px 4px !important' },
              tableLayout: 'auto'
            }}
          >
            <TableHead>
              <TableRow>
                {/* Match old domains table order: Checkbox first, then all company columns in order */}
                <TableCell sx={{ padding: '8px 6px', backgroundColor: '#fff' }}>
                  <Typography variant="caption" sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                    Save for Later
                  </Typography>
                </TableCell>
                <TableCell sx={{ padding: '8px 6px', backgroundColor: '#fff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleSort('website')}>
                    Website
                    {sortField === 'website' && (
                      sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ padding: '8px 6px', backgroundColor: '#fff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleSort('name')}>
                    Company Name
                    {sortField === 'name' && (
                      sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ padding: '8px 6px', backgroundColor: '#fff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleSort('clean_name')}>
                    Clean Name
                    {sortField === 'clean_name' && (
                      sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ padding: '8px 6px', backgroundColor: '#fff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleSort('note')}>
                    Notes
                    {sortField === 'note' && (
                      sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ padding: '8px 6px', backgroundColor: '#fff', minWidth: 300 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleSort('description')}>
                    Description
                    {sortField === 'description' && (
                      sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                    )}
                  </Box>
                </TableCell>
                <TableCell align="right" sx={{ padding: '8px 6px', backgroundColor: '#fff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleSort('unsubscribe')}>
                    Unsubscribe
                    {sortField === 'unsubscribe' && (
                      sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                    )}
                  </Box>
                </TableCell>
                <TableCell align="right" sx={{ padding: '8px 6px', backgroundColor: '#fff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleSort('in_mailer')}>
                    Inmailer
                    {sortField === 'in_mailer' && (
                      sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ padding: '8px 6px', backgroundColor: '#fff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleSort('state_name')}>
                    State
                    {sortField === 'state_name' && (
                      sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ padding: '8px 6px', backgroundColor: '#fff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleSort('location')}>
                    Location
                    {sortField === 'location' && (
                      sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ padding: '8px 6px', backgroundColor: '#fff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleSort('country_name')}>
                    Country
                    {sortField === 'country_name' && (
                      sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ padding: '8px 6px', backgroundColor: '#fff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleSort('company_address')}>
                    Company Address
                    {sortField === 'company_address' && (
                      sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ padding: '8px 6px', backgroundColor: '#fff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleSort('snov_id')}>
                    Snov ID
                    {sortField === 'snov_id' && (
                      sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ padding: '8px 6px', backgroundColor: '#fff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleSort('industry_tag_name')}>
                    Industry Tag
                    {sortField === 'industry_tag_name' && (
                      sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ padding: '8px 6px', backgroundColor: '#fff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleSort('industry_tag_two_name')}>
                    Industry Tag Two
                    {sortField === 'industry_tag_two_name' && (
                      sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ padding: '8px 6px', backgroundColor: '#fff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleSort('industry_tag_three_name')}>
                    Industry Tag Three
                    {sortField === 'industry_tag_three_name' && (
                      sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ padding: '8px 6px', backgroundColor: '#fff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleSort('industry_tag_four_name')}>
                    Industry Tag Four
                    {sortField === 'industry_tag_four_name' && (
                      sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                    )}
                  </Box>
                </TableCell>
                 <TableCell align="right" sx={{ padding: '8px 6px', backgroundColor: '#fff', minWidth: 70 }}>
                   <Tooltip title="Deliverable" arrow>
                     <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', justifyContent: 'flex-end' }} onClick={() => handleSort('deliverable')}>
                       Del
                       {sortField === 'deliverable' && (
                         sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                       )}
                     </Box>
                   </Tooltip>
                 </TableCell>
                 <TableCell align="right" sx={{ padding: '8px 6px', backgroundColor: '#fff', minWidth: 70 }}>
                   <Tooltip title="Risky" arrow>
                     <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', justifyContent: 'flex-end' }} onClick={() => handleSort('risky')}>
                       Ris
                       {sortField === 'risky' && (
                         sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                       )}
                     </Box>
                   </Tooltip>
                 </TableCell>
                 <TableCell align="right" sx={{ padding: '8px 6px', backgroundColor: '#fff', minWidth: 70 }}>
                   <Tooltip title="Undeliverable" arrow>
                     <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', justifyContent: 'flex-end' }} onClick={() => handleSort('undeliverable')}>
                       Und
                       {sortField === 'undeliverable' && (
                         sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                       )}
                     </Box>
                   </Tooltip>
                 </TableCell>
                 <TableCell align="right" sx={{ padding: '8px 6px', backgroundColor: '#fff', minWidth: 70 }}>
                   <Tooltip title="Bounce" arrow>
                     <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', justifyContent: 'flex-end' }} onClick={() => handleSort('bounce')}>
                       Bou
                       {sortField === 'bounce' && (
                         sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                       )}
                     </Box>
                   </Tooltip>
                 </TableCell>
                <TableCell sx={{ padding: '8px 6px', backgroundColor: '#fff', minWidth: 60 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleSort('Linkedin')}>
                    LI
                    {sortField === 'Linkedin' && (
                      sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ padding: '8px 6px', backgroundColor: '#fff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleSort('employees_onlinkedin')}>
                    Employees on LinkedIn
                    {sortField === 'employees_onlinkedin' && (
                      sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ padding: '8px 6px', backgroundColor: '#fff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleSort('linkedin_id')}>
                    LinkedIn ID
                    {sortField === 'linkedin_id' && (
                      sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ padding: '8px 6px', backgroundColor: '#fff', minWidth: 60 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleSort('twitter')}>
                    TW
                    {sortField === 'twitter' && (
                      sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ padding: '8px 6px', backgroundColor: '#fff', minWidth: 60 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleSort('Facebook')}>
                    FB
                    {sortField === 'Facebook' && (
                      sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ padding: '8px 6px', backgroundColor: '#fff', minWidth: 60 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleSort('ig')}>
                    IG
                    {sortField === 'ig' && (
                      sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ padding: '8px 6px', backgroundColor: '#fff', minWidth: 60 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleSort('Youtube')}>
                    YT
                    {sortField === 'Youtube' && (
                      sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ padding: '8px 6px', backgroundColor: '#fff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleSort('license')}>
                    License
                    {sortField === 'license' && (
                      sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ padding: '8px 6px', backgroundColor: '#fff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleSort('phone')}>
                    Phone
                    {sortField === 'phone' && (
                      sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                    )}
                  </Box>
                </TableCell>
                <TableCell align="right" sx={{ padding: '8px 6px', backgroundColor: '#fff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleSort('unknown')}>
                    Unknown
                    {sortField === 'unknown' && (
                      sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                    )}
                  </Box>
                </TableCell>
                <TableCell align="right" sx={{ padding: '8px 6px', backgroundColor: '#fff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleSort('not_verified')}>
                    Not Verified
                    {sortField === 'not_verified' && (
                      sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ padding: '8px 6px', backgroundColor: '#fff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleSort('suspicious')}>
                    Suspicious
                    {sortField === 'suspicious' && (
                      sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ padding: '8px 6px', backgroundColor: '#fff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleSort('type_')}>
                    Type
                    {sortField === 'type_' && (
                      sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ padding: '8px 6px', backgroundColor: '#fff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleSort('status')}>
                    Status
                    {sortField === 'status' && (
                      sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedResults
                .slice(0, displayCount)
                .map((result: any) => (
                  <TableRow key={result.id} hover>
                    <TableCell sx={{ padding: '8px 6px' }}>
                      <Checkbox
                        checked={selectedResults.has(result.id)}
                        onChange={() => handleSelectResult(result.id)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'medium', padding: '8px 6px' }}>{renderCompactLink(result.website || result.domain, 'website')}</TableCell>
                    <TableCell sx={{ padding: '8px 6px' }}>{result.name || result.company || '-'}</TableCell>
                    <TableCell sx={{ padding: '8px 6px' }}>{result.clean_name || '-'}</TableCell>
                    <TableCell sx={{ padding: '8px 6px' }}>
                      <Tooltip title={String(result.note || result.notes || '')} arrow>
                        <span>{truncateText(String(result.note || result.notes || ''), 30)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ padding: '8px 6px' }}>
                      <Tooltip title={String(result.description || '')} arrow>
                        <span>{truncateText(String(result.description || ''), 30)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right" sx={{ padding: '8px 6px' }}>{result.unsubscribe ?? '-'}</TableCell>
                    <TableCell align="right" sx={{ padding: '8px 6px' }}>{result.in_mailer ?? '-'}</TableCell>
                    <TableCell sx={{ padding: '8px 6px' }}>{result.state_name || '-'}</TableCell>
                    <TableCell sx={{ padding: '8px 6px' }}>{result.location || '-'}</TableCell>
                    <TableCell sx={{ padding: '8px 6px' }}>{result.country_name || '-'}</TableCell>
                    <TableCell sx={{ padding: '8px 6px' }}>
                      <Tooltip title={String(result.company_address || '')} arrow>
                        <span>{truncateText(String(result.company_address || ''), 30)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ padding: '8px 6px' }}>{result.snov_id || '-'}</TableCell>
                    <TableCell sx={{ padding: '8px 6px' }}>{result.industry_tag_name || result.c_tag1 || '-'}</TableCell>
                    <TableCell sx={{ padding: '8px 6px' }}>{result.industry_tag_two_name || result.c_tag2 || '-'}</TableCell>
                    <TableCell sx={{ padding: '8px 6px' }}>{result.industry_tag_three_name || result.c_tag3 || '-'}</TableCell>
                    <TableCell sx={{ padding: '8px 6px' }}>{result.industry_tag_four_name || result.c_tag4 || '-'}</TableCell>
                    <TableCell align="right" sx={{ padding: '8px 6px' }}>{result.deliverable ?? '-'}</TableCell>
                    <TableCell align="right" sx={{ padding: '8px 6px' }}>{result.risky ?? '-'}</TableCell>
                    <TableCell align="right" sx={{ padding: '8px 6px' }}>{result.undeliverable ?? '-'}</TableCell>
                    <TableCell align="right" sx={{ padding: '8px 6px' }}>{result.bounce ?? '-'}</TableCell>
                    <TableCell sx={{ padding: '8px 6px' }}>{renderCompactLink(result.Linkedin, 'linkedin')}</TableCell>
                    <TableCell sx={{ padding: '8px 6px' }}>{result.employees_onlinkedin || '-'}</TableCell>
                    <TableCell sx={{ padding: '8px 6px' }}>{result.linkedin_id || '-'}</TableCell>
                    <TableCell sx={{ padding: '8px 6px' }}>{renderCompactLink(result.twitter, 'twitter')}</TableCell>
                    <TableCell sx={{ padding: '8px 6px' }}>{renderCompactLink(result.Facebook, 'Facebook')}</TableCell>
                    <TableCell sx={{ padding: '8px 6px' }}>{renderCompactLink(result.ig, 'ig')}</TableCell>
                    <TableCell sx={{ padding: '8px 6px' }}>{renderCompactLink(result.Youtube, 'Youtube')}</TableCell>
                    <TableCell sx={{ padding: '8px 6px' }}>{result.license || '-'}</TableCell>
                    <TableCell sx={{ padding: '8px 6px' }}>{result.phone || '-'}</TableCell>
                    <TableCell align="right" sx={{ padding: '8px 6px' }}>{result.unknown ?? '-'}</TableCell>
                    <TableCell align="right" sx={{ padding: '8px 6px' }}>{result.not_verified ?? '-'}</TableCell>
                    <TableCell sx={{ padding: '8px 6px' }}>{result.suspicious || '-'}</TableCell>
                    <TableCell sx={{ padding: '8px 6px' }}>{result.type_ || result.type || '-'}</TableCell>
                    <TableCell sx={{ padding: '8px 6px' }}>{result.status || '-'}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ p: 1, textAlign: 'center', fontSize: '0.875rem', color: 'text.secondary' }}>
          Showing {displayCount} of {mockResults.length}
        </Box>
      </Paper>
    </Box>
  );
};

export default OutreachResultsPage;

